import { isLocal } from "@/config";
import { isStaleDynamicImportError } from "@/lib/utils/vitePreloadError";
import { loadDatadog, markDatadogAsInitialized } from "./datadog";
import { logSystemInfo, setupSystemInfoListeners } from "./logSystemInfo";

interface RumErrorEventLike {
  type?: string;
  message?: string;
  error?: {
    handling?: string;
    message?: string;
    source?: string;
    source_type?: string;
    stack?: string;
  };
}

const BROWSER_OBJECT_NOT_FOUND_PATTERN =
  /^Uncaught "Object Not Found Matching Id:\d+, MethodName:update, ParamCount:4"$/;
const BROWSER_RESIZE_OBSERVER_LOOP_MESSAGE =
  'Uncaught "ResizeObserver loop completed with undelivered notifications."';
const RECOVERABLE_CONSOLE_NETWORK_ERROR_PATTERNS = [
  /CombinedError: \[Network\] (?:Failed to fetch|Load failed|Session expired|undefined)/,
  /\[Network\] (?:Failed to fetch|Load failed|Session expired|undefined)(?: \(naapi\.prod\.handraise\.site\))?/,
  /^Error: Network error, please try again\.$/,
  /\berror: \[Network\] (?:Failed to fetch|Load failed|Session expired)/,
  /^Error (?:fetching|refreshing)[^:]*: (?:CombinedError: )?\[Network\]/,
  /^Error refreshing token: TypeError: Failed to fetch/,
  /^Failed to fetch me: CombinedError: \[Network\]/,
];
const RECOVERABLE_CONSOLE_WEBSOCKET_ERROR_PATTERNS = [
  /^Subscription error:[\s\S]*"\[WebSocket\]"/,
  /^WebSocket connection (?:closed unexpectedly|error):[\s\S]*"\[WebSocket\]"/,
];
const RECOVERABLE_CONSOLE_THIRD_PARTY_ERROR_PATTERNS = [
  /^Amplitude Logger \[Error\]: Failed to fetch/,
  /^TypeError: undefined is not an object \(evaluating '.*\._result\.default'\)/,
];
const EXPECTED_AUTH_CONSOLE_ERROR_PATTERNS = [
  /^CombinedError: \[GraphQL\] Invalid email or password\b/,
  /^Error: The username or password you entered is incorrect\. Please try again\.$/,
  /^Error: There was an issue logging in with Google\. Please retry or use a different login method\.$/,
];
const EXPECTED_CSP_REPORT_ERROR_PATTERNS = [
  /^csp_violation: 'blob' blocked by 'img-src' directive$/,
  /^csp_violation: 'https:\/\/archive\.tveyes\.com' blocked by 'frame-src' directive$/,
];
const GRAPHQL_WS_PROTOCOL_CLOSE_PATTERN = /"code":\s*44\d\d/;
const CHROME_EXTENSION_TOP_FRAME_PATTERN =
  /^\s*(?:at\s+)?[^\n]*chrome-extension:\/\//;
const UI_COMPONENTS_URQL_STACK_PATTERN =
  /^[A-Za-z][A-Za-z0-9]{1,2}@https:\/\/app\.handraise\.com\/ui-components\.[^/\s]+\.js:\d+:\d+/;
const UI_COMPONENTS_NETWORK_TRANSPORT_MARKERS = [
  /\[Network\]/,
  /Failed to fetch/,
  /Load failed/,
  /Session expired/,
  /Network error, please try again/,
];

const isChromeExtensionOriginatedStack = (stack: string): boolean => {
  const firstFrameLine = stack
    .split("\n")
    .slice(1)
    .find((line) => line.trim().length > 0);

  return (
    firstFrameLine !== undefined &&
    CHROME_EXTENSION_TOP_FRAME_PATTERN.test(firstFrameLine)
  );
};

const isUiComponentsUrqlNetworkTransportError = (
  errorMessage: string,
): boolean =>
  UI_COMPONENTS_URQL_STACK_PATTERN.test(errorMessage) &&
  UI_COMPONENTS_NETWORK_TRANSPORT_MARKERS.some((pattern) =>
    pattern.test(errorMessage),
  );

/**
 * Drops handled console telemetry for recoverable transport failures.
 */
export const shouldDropRecoverableConsoleRumError = (
  event: RumErrorEventLike,
): boolean => {
  if (event.type !== undefined && event.type !== "error") {
    return false;
  }

  const error = event.error;

  if (!error || error.handling !== "handled" || error.source !== "console") {
    return false;
  }

  const errorMessage = error.message ?? event.message ?? "";

  if (GRAPHQL_WS_PROTOCOL_CLOSE_PATTERN.test(errorMessage)) {
    return false;
  }

  return (
    RECOVERABLE_CONSOLE_NETWORK_ERROR_PATTERNS.some((pattern) =>
      pattern.test(errorMessage),
    ) ||
    RECOVERABLE_CONSOLE_WEBSOCKET_ERROR_PATTERNS.some((pattern) =>
      pattern.test(errorMessage),
    ) ||
    RECOVERABLE_CONSOLE_THIRD_PARTY_ERROR_PATTERNS.some((pattern) =>
      pattern.test(errorMessage),
    ) ||
    EXPECTED_AUTH_CONSOLE_ERROR_PATTERNS.some((pattern) =>
      pattern.test(errorMessage),
    ) ||
    isUiComponentsUrqlNetworkTransportError(errorMessage)
  );
};

/**
 * Drops handled stale-chunk telemetry after the app schedules a clean reload.
 */
export const shouldDropStaleDynamicImportRumError = (
  event: RumErrorEventLike,
): boolean => {
  if (event.type !== undefined && event.type !== "error") {
    return false;
  }

  const error = event.error;

  return (
    error?.handling === "handled" &&
    error.source === "console" &&
    isStaleDynamicImportError(error.message ?? event.message ?? "")
  );
};

/**
 * Drops browser diagnostics that have no app stack and cannot be acted on.
 */
export const shouldDropBrowserDiagnosticRumError = (
  event: RumErrorEventLike,
): boolean => {
  if (event.type !== undefined && event.type !== "error") {
    return false;
  }

  const error = event.error;

  if (
    error?.handling === "unhandled" &&
    error.source === "source" &&
    error.source_type === "browser"
  ) {
    if (
      (BROWSER_OBJECT_NOT_FOUND_PATTERN.test(error.message ?? "") &&
        error.stack === "No stack, consider using an instance of Error") ||
      error.message === BROWSER_RESIZE_OBSERVER_LOOP_MESSAGE ||
      isChromeExtensionOriginatedStack(error.stack ?? "")
    ) {
      return true;
    }
  }

  if (
    error?.handling === "unhandled" &&
    error.source === "report" &&
    EXPECTED_CSP_REPORT_ERROR_PATTERNS.some((pattern) =>
      pattern.test(error.message ?? event.message ?? ""),
    )
  ) {
    return true;
  }

  return false;
};

export const initializeAnalytics = () => {
  // Only run analytics in production and stage environments
  if (isLocal) {
    console.log("Skipping analytics in local environment");
    return () => {};
  }

  // Create an array to store cleanup functions
  const cleanupFunctions: Array<() => void> = [];

  loadDatadog().then((datadogRum) => {
    datadogRum.init({
      applicationId: "cad0c07e-468f-41c0-a96a-b271c04d6325",
      clientToken: "pubbdfc955a344c1f8eae4f7b37d55c10e5",
      site: "us5.datadoghq.com",
      service: "newsamp",
      env: `${import.meta.env.VITE_ENVIRONMENT}`,
      // Specify a version number to identify the deployed version of your application in Datadog
      // version: '1.0.0',
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: "mask-user-input",
      enableExperimentalFeatures: ["feature_flags"],
      actionNameAttribute: "data-dd-action-name",
      compressIntakeRequests: true,
      usePartitionedCrossSiteSessionCookie: true,
      beforeSend: (event) => {
        return !(
          shouldDropRecoverableConsoleRumError(event) ||
          shouldDropStaleDynamicImportRumError(event) ||
          shouldDropBrowserDiagnosticRumError(event)
        );
      },
    });
    markDatadogAsInitialized();

    // Start session replay
    try {
      datadogRum.startSessionReplayRecording();
    } catch (error: unknown) {
      console.error("Failed to start session replay recording:", error);
    }

    logSystemInfo();
    const cleanupListeners = setupSystemInfoListeners();
    cleanupFunctions.push(cleanupListeners);
  });

  return () => {
    for (const cleanup of cleanupFunctions) {
      cleanup();
    }
  };
};
